/**
 * London Smoke & Cure — Njord Cloud Run history and registry API.
 */

import { getNjordCloudRunUrl, njordAdminHeaders } from './njordConfig';
import { njordSensorsGetCache } from './njordRegistryGetCache';

async function parseJson(res) {
    const text = await res.text();
    let data;
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`Njord API non-JSON (${res.status}): ${text.slice(0, 200)}`);
    }
    if (!res.ok) {
        throw new Error(data.error || data.message || `HTTP ${res.status}`);
    }
    return data;
}

export async function fetchNjordSensors(opts = {}) {
    const includeArchived = Boolean(opts.includeArchived);
    const key = includeArchived ? 'all' : 'active';
    return njordSensorsGetCache.get(
        key,
        async () => {
            const base = getNjordCloudRunUrl();
            if (!base) throw new Error('REACT_APP_NJORD_CLOUD_RUN_URL is not set');
            const q = includeArchived ? '?includeArchived=1' : '';
            const res = await fetch(`${base}/api/sensors${q}`, {
                headers: njordAdminHeaders(),
                cache: 'no-store',
            });
            const data = await parseJson(res);
            return Array.isArray(data.sensors) ? data.sensors : [];
        },
        { force: Boolean(opts.force) }
    );
}

/**
 * @param {string} sensorId
 * @param {{ since?: number, limit?: number }} [opts]
 */
export async function fetchNjordReadings(sensorId, opts = {}) {
    const base = getNjordCloudRunUrl();
    if (!base) throw new Error('REACT_APP_NJORD_CLOUD_RUN_URL is not set');
    const q = new URLSearchParams();
    if (opts.since) q.set('since', String(opts.since));
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    const res = await fetch(`${base}/api/sensors/${encodeURIComponent(sensorId)}/readings${qs ? `?${qs}` : ''}`, {
        headers: njordAdminHeaders(),
        cache: 'no-store',
    });
    return parseJson(res);
}

/**
 * @param {string} sensorId
 * @param {string} displayName
 * @param {{ deviceClass?: string }} [opts] — e.g. `scale` (njord-service v1.1.11+)
 */
export async function registerNjordDevice(sensorId, displayName, opts = {}) {
    const base = getNjordCloudRunUrl();
    if (!base) throw new Error('REACT_APP_NJORD_CLOUD_RUN_URL is not set');
    const body = { sensorId, displayName };
    const deviceClass = opts && opts.deviceClass != null ? String(opts.deviceClass).trim() : '';
    if (deviceClass) body.deviceClass = deviceClass;
    const res = await fetch(`${base}/api/devices/register`, {
        method: 'POST',
        headers: { ...njordAdminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    njordSensorsGetCache.invalidate();
    return data;
}

/**
 * @param {string} sensorId
 * @param {object} patch
 */
export async function patchNjordRegistry(sensorId, patch) {
    const base = getNjordCloudRunUrl();
    if (!base) throw new Error('REACT_APP_NJORD_CLOUD_RUN_URL is not set');
    const res = await fetch(`${base}/api/sensors/${encodeURIComponent(sensorId)}/registry`, {
        method: 'PATCH',
        headers: { ...njordAdminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
    });
    const data = await parseJson(res);
    njordSensorsGetCache.invalidate();
    return data;
}

export async function postNjordDesiredViaApi(sensorId, patch, updatedBy = '') {
    const base = getNjordCloudRunUrl();
    if (!base) throw new Error('REACT_APP_NJORD_CLOUD_RUN_URL is not set');
    const res = await fetch(`${base}/api/sensors/${encodeURIComponent(sensorId)}/desired`, {
        method: 'POST',
        headers: { ...njordAdminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...patch, updatedBy }),
    });
    return parseJson(res);
}
